import React, { useState } from 'react';
import Popover from './Popover';
import { Icon } from '../icons';
import { useT } from '../../i18n';

/* ---- date helpers (no external dependency) -------------------------------- */
const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a, b) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']; // week starts Monday

function monthMatrix(monthDate) {
  const first = startOfMonth(monthDate);
  const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startWeekday);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
}

const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const DEFAULT_PRESETS = [
  { label: 'Today', range: () => { const t = stripTime(new Date()); return { from: t, to: t }; } },
  { label: 'Next 7 days', range: () => { const t = stripTime(new Date()); const e = new Date(t); e.setDate(t.getDate() + 6); return { from: t, to: e }; } },
  { label: 'This month', range: () => { const t = new Date(); return { from: startOfMonth(t), to: new Date(t.getFullYear(), t.getMonth() + 1, 0) }; } },
  { label: 'Next month', range: () => { const t = addMonths(new Date(), 1); return { from: startOfMonth(t), to: new Date(t.getFullYear(), t.getMonth() + 1, 0) }; } },
];

const MonthGrid = ({ monthDate, from, to, hover, onPick, onHover }) => {
  const t = useT();
  const days = monthMatrix(monthDate);
  const month = monthDate.getMonth();
  const today = stripTime(new Date());
  const rangeEnd = to || hover;

  return (
    <div className="ps-drp__month">
      <div className="ps-drp__month-title">{t(MONTHS[month])} {monthDate.getFullYear()}</div>
      <div className="ps-drp__weekdays">
        {WEEKDAYS.map((w) => <span key={w} className="ps-drp__weekday">{t(w)}</span>)}
      </div>
      <div className="ps-drp__grid">
        {days.map((d) => {
          const outside = d.getMonth() !== month;
          const isFrom = sameDay(d, from);
          const isTo = sameDay(d, to);
          const inRange = from && rangeEnd && d > from && d < rangeEnd;
          const isEndpoint = isFrom || isTo;
          const cls = [
            'ps-drp__day',
            outside ? 'is-outside' : '',
            sameDay(d, today) ? 'is-today' : '',
            isEndpoint ? 'is-selected' : '',
            isFrom && rangeEnd && !sameDay(from, rangeEnd) ? 'is-start' : '',
            isTo && from && !sameDay(from, rangeEnd) ? 'is-end' : '',
            inRange ? 'in-range' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={toISO(d)}
              type="button"
              className={cls}
              onClick={() => onPick(d)}
              onMouseEnter={() => onHover(d)}
              tabIndex={outside ? -1 : 0}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * DateRangePicker — start/end date selection in a calendar popover.
 *
 * Props:
 *   value: { from, to }            — ISO 'YYYY-MM-DD' strings
 *   onChange({ from, to })
 *   numberOfMonths: 1 | 2          — default 2
 *   presets: Array<{ label, range:() => {from:Date,to:Date} }> | false
 *   placeholder, size, disabled, ariaLabel
 */
const DateRangePicker = ({
  value,
  onChange,
  numberOfMonths = 2,
  presets = DEFAULT_PRESETS,
  placeholder,
  size = 'md',
  disabled = false,
  ariaLabel,
  className = '',
}) => {
  const t = useT();
  const from = fromISO(value?.from);
  const to = fromISO(value?.to);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(from || new Date()));
  const [hover, setHover] = useState(null);

  const pick = (day) => {
    if (!from || (from && to)) {
      onChange?.({ from: toISO(day), to: '' });
    } else if (day < from) {
      onChange?.({ from: toISO(day), to: toISO(from) });
    } else {
      onChange?.({ from: toISO(from), to: toISO(day) });
    }
  };

  const applyPreset = (preset) => {
    const r = preset.range();
    onChange?.({ from: toISO(r.from), to: toISO(r.to) });
    setViewMonth(startOfMonth(r.from));
  };

  const label = from
    ? (to ? `${fmt(from)} → ${fmt(to)}` : `${fmt(from)} → …`)
    : (placeholder ?? t('Select dates'));

  const trigger = (
    <button
      type="button"
      className={`ps-select ps-select--${size} ps-drp__trigger ${disabled ? 'is-disabled' : ''}`.trim()}
      disabled={disabled}
      aria-label={ariaLabel || t('Select date range')}
    >
      <Icon.Calendar size={16} className="ps-drp__trigger-icon" />
      <span className={`ps-select__value ${from ? '' : 'ps-select__value--placeholder'}`.trim()}>{label}</span>
      <Icon.ChevronDown size={16} className="ps-select__caret" />
    </button>
  );

  return (
    <Popover trigger={trigger} placement="bottom-start" className={className} panelClassName="ps-popover__panel--drp">
      {() => (
        <div className="ps-drp">
          {presets && (
            <div className="ps-drp__presets">
              {presets.map((p) => (
                <button key={p.label} type="button" className="ps-drp__preset" onClick={() => applyPreset(p)}>
                  {t(p.label)}
                </button>
              ))}
            </div>
          )}
          <div className="ps-drp__cal">
            <div className="ps-drp__nav">
              <button type="button" className="ps-drp__navbtn" aria-label={t('Previous month')} onClick={() => setViewMonth((m) => addMonths(m, -1))}>
                <Icon.ChevronLeft size={16} />
              </button>
              <button type="button" className="ps-drp__navbtn" aria-label={t('Next month')} onClick={() => setViewMonth((m) => addMonths(m, 1))}>
                <Icon.ChevronRight size={16} />
              </button>
            </div>
            <div className="ps-drp__months" onMouseLeave={() => setHover(null)}>
              {Array.from({ length: numberOfMonths }, (_, i) => (
                <MonthGrid
                  key={i}
                  monthDate={addMonths(viewMonth, i)}
                  from={from}
                  to={to}
                  hover={hover}
                  onPick={pick}
                  onHover={setHover}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </Popover>
  );
};

export default DateRangePicker;
